/**
 * Recurring Pattern Operations
 *
 * Handles all recurring pattern management operations (dismissals, snoozes, metadata)
 */

import { query, queryOne, execute, saveDB } from '../../db';

/**
 * Dismiss a recurring pattern
 */
export async function dismissPattern(pattern: string): Promise<{ success: boolean; error?: string }> {
  try {
    execute(
      'INSERT OR REPLACE INTO dismissed_patterns (pattern, dismissed_at) VALUES (?, ?)',
      [pattern, new Date().toISOString()]
    );
    await saveDB();
    return { success: true };
  } catch (error) {
    console.error('[recurringOperations] Error dismissing pattern:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to dismiss pattern' };
  }
}

/**
 * Get all dismissed patterns
 */
export async function getDismissedPatterns(): Promise<string[]> {
  try {
    const rows = query<{ pattern: string }>('SELECT pattern FROM dismissed_patterns');
    return rows.map(r => r.pattern);
  } catch (error) {
    console.error('[recurringOperations] Error getting dismissed patterns:', error);
    return [];
  }
}

/**
 * Undismiss a recurring pattern
 */
export async function undismissPattern(pattern: string): Promise<{ success: boolean; error?: string }> {
  try {
    execute('DELETE FROM dismissed_patterns WHERE pattern = ?', [pattern]);
    await saveDB();
    return { success: true };
  } catch (error) {
    console.error('[recurringOperations] Error undismissing pattern:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to undismiss pattern' };
  }
}

/**
 * Snooze a recurring pattern for a number of days
 */
export async function snoozePattern(pattern: string, days: number): Promise<{ success: boolean; error?: string }> {
  try {
    const snoozedUntil = new Date();
    snoozedUntil.setDate(snoozedUntil.getDate() + days);

    execute(
      'INSERT OR REPLACE INTO snoozed_patterns (pattern, snoozed_until, snoozed_at) VALUES (?, ?, ?)',
      [pattern, snoozedUntil.toISOString(), new Date().toISOString()]
    );
    await saveDB();
    return { success: true };
  } catch (error) {
    console.error('[recurringOperations] Error snoozing pattern:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to snooze pattern' };
  }
}

/**
 * Get all snoozed patterns (only active snoozes)
 */
export async function getSnoozedPatterns(): Promise<{ pattern: string; snoozedUntil: string }[]> {
  try {
    const now = new Date().toISOString();
    // Only return patterns that are still snoozed
    const rows = query<{ pattern: string; snoozed_until: string }>(
      'SELECT pattern, snoozed_until FROM snoozed_patterns WHERE snoozed_until > ?',
      [now]
    );

    // Clean up expired snoozes
    execute('DELETE FROM snoozed_patterns WHERE snoozed_until <= ?', [now]);
    await saveDB();

    return rows.map(r => ({ pattern: r.pattern, snoozedUntil: r.snoozed_until }));
  } catch (error) {
    console.error('[recurringOperations] Error getting snoozed patterns:', error);
    return [];
  }
}

/**
 * Update custom interval for a recurring pattern
 */
export async function updateInterval(pattern: string, customInterval: number): Promise<{ success: boolean; error?: string }> {
  try {
    execute(
      'INSERT OR REPLACE INTO recurring_metadata (pattern, custom_interval, updated_at) VALUES (?, ?, ?)',
      [pattern, customInterval, new Date().toISOString()]
    );
    await saveDB();
    return { success: true };
  } catch (error) {
    console.error('[recurringOperations] Error updating interval:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to update interval' };
  }
}

/**
 * Get recurring metadata for a pattern
 */
export async function getMetadata(pattern: string): Promise<{ customInterval?: number } | null> {
  try {
    const row = queryOne<{ custom_interval: number | null }>(
      'SELECT custom_interval FROM recurring_metadata WHERE pattern = ?',
      [pattern]
    );

    if (!row) return null;

    return {
      customInterval: row.custom_interval !== null ? row.custom_interval : undefined
    };
  } catch (error) {
    console.error('[recurringOperations] Error getting recurring metadata:', error);
    return null;
  }
}
